// ============================================================================
// ARKO Vertragsgenerator — Frontend-Schema-Mirror + generische Helfer
// ----------------------------------------------------------------------------
// Quelle der Wahrheit ist `data/contract-templates/schema/mietvertrag.schema.json`
// (Stream A, Branch pka/contract-gen-templates-engine). Dieses Modul SPIEGELT den
// relevanten Schema-Teil (title/description/enum/required/if-then) als typisierte
// TS-Konstante, damit das Portal-Frontend ohne Cross-Repo-JSON-Import (Vite
// fs.allow / resolveJsonModule) build-sicher bleibt. Gleiches Muster wie der
// dokumentierte „Frontend-Mirror" der Intent-Heuristik.
//
// WICHTIG (Sync-Pflicht): Aendert sich `mietvertrag.schema.json` (neue Pflicht-
// felder, Enums, if/then), muss MIETVERTRAG_SCHEMA hier identisch nachgezogen
// werden. Der Backend-Vertrag (Engine A / Workflow B) bleibt die Wahrheit.
//
// Das Modul ist generisch (JSON-Schema-draft-07-Teilmenge): Formular (Stream C)
// UND Chat-Slot-Filling (Stream D) leiten Felder, Reihenfolge, Pflicht und
// Validierung daraus ab. Neuer Vertragstyp = weiteres Schema in CONTRACT_TEMPLATES.
// ============================================================================

import { individuellOutputItems, type EigeneKlausel } from './contractDraftStore.ts';
import { GERUEST_TEMPLATES } from './contractGeruest.ts';

export type JsonSchemaType = 'object' | 'string' | 'number' | 'boolean' | 'array';

export interface SchemaProp {
  type: JsonSchemaType;
  title?: string;
  description?: string;
  enum?: string[];
  format?: string;
  minLength?: number;
  maxLength?: number;
  minimum?: number;
  maximum?: number;
  exclusiveMinimum?: number;
  default?: unknown;
  required?: string[];
  properties?: Record<string, SchemaProp>;
  items?: SchemaProp;
}

export interface ContractSchema {
  $id?: string;
  title: string;
  description?: string;
  type: 'object';
  required: string[];
  properties: Record<string, SchemaProp>;
  if?: { properties?: Record<string, { const?: unknown }>; required?: string[] };
  then?: { required?: string[] };
}

/** engine = n8n ARKO_CONTRACT_GENERATE (mietvertrag.docx). geruest = UI-Slots, Muster folgt. */
export type ContractMaturity = 'engine' | 'geruest';

export interface ContractBlockDef {
  id: string;
  label: string;
  hint: string;
  /** Top-Level-Schema-Keys, die dieser Baustein ein-/ausblendet. */
  fieldKeys: string[];
  defaultEnabled: boolean;
  /** Pflicht-Baustein, nicht abschaltbar. */
  locked?: boolean;
  /** Nur Extra-Slots (kein Schema-Feld) — reines Gerüst. */
  extraOnly?: boolean;
  /** Wenn gesetzt: Block-Toggle schreibt dieses Boolean-Feld. */
  bindsBoolean?: string;
}

/** Polar CONTRACT_INHALTE_STATE_COLORS: fix=blau, an=leicht grün, aus=leicht rot. */
export type InhalteRowTone = 'fix' | 'an' | 'aus';

export function inhalteRowTone(locked: boolean, enabled: boolean): InhalteRowTone {
  if (locked) return 'fix';
  return enabled ? 'an' : 'aus';
}

/** Bestehende Portal-Tailwind-Tokens — kein Neon, kein Taste-Skill. */
export const INHALTE_ROW_TONE_CLASS: Record<InhalteRowTone, string> = {
  fix: 'bg-blue-600/20 border-blue-500/50 text-blue-200',
  an: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
  aus: 'bg-red-500/10 border-red-500/30 text-red-300',
};

/**
 * Polar CONTRACT_HOVER_SINGLE: genau ein Hover-Preview.
 * enter setzt die neue Zeile; leave der alten nach enter der neuen lässt die neue stehen.
 */
export function nextInhalteHoverId(
  current: string | null,
  targetId: string,
  kind: 'enter' | 'leave',
): string | null {
  if (kind === 'enter') return targetId;
  return current === targetId ? null : current;
}

export interface ContractExtraSlotDef {
  id: string;
  blockId: string;
  label: string;
  kind: 'boolean' | 'enum' | 'string';
  enum?: string[];
  placeholder?: string;
}

export interface ContractTemplateDef {
  id: string;
  label: string;
  /** Kurzbeschreibung für die Typ-Auswahl. */
  blurb: string;
  schema: ContractSchema;
  maturity: ContractMaturity;
  blocks: ContractBlockDef[];
  extraSlots?: ContractExtraSlotDef[];
  /** Enum-Schlüssel → Label (Sonderklauseln). */
  clauseLabels?: Record<string, string>;
  /** Word-Rohquelle im Repo. Anzeige im Portal: ARKO-Vorlage. Kein Engine-docx, kein ARKORBIT-AGB. */
  sourceMuster?: {
    label: string;
    repoPath: string;
  };
}

// ── Mietvertrag (Österreich) — Spiegel von schema/mietvertrag.schema.json ──────
export const MIETVERTRAG_SCHEMA: ContractSchema = {
  $id: 'https://arkorbit.at/contract-templates/schema/mietvertrag.schema.json',
  title: 'ARKO Vertragsgenerator — Mietvertrag (Österreich)',
  type: 'object',
  required: [
    'vermieter',
    'mieter',
    'objekt',
    'mietbeginn',
    'befristet',
    'hauptmietzins_eur',
    'betriebskosten_eur',
  ],
  properties: {
    vermieter: {
      type: 'object',
      title: 'Vermieter',
      description: 'Vermietende Partei (natürliche oder juristische Person).',
      required: ['name', 'anschrift'],
      properties: {
        name: {
          type: 'string',
          minLength: 1,
          maxLength: 200,
          title: 'Name des Vermieters',
          description:
            'Vollständiger Name bzw. Firmenwortlaut der vermietenden Partei (z. B. „Nordlicht Immobilien GmbH" oder „Maria Muster").',
        },
        anschrift: {
          type: 'string',
          minLength: 1,
          maxLength: 300,
          title: 'Anschrift des Vermieters',
          description: 'Zustelladresse des Vermieters (Straße, Hausnummer, PLZ, Ort), z. B. „Handelskai 99, 1020 Wien".',
        },
      },
    },
    mieter: {
      type: 'object',
      title: 'Mieter',
      description: 'Mietende Partei (natürliche oder juristische Person).',
      required: ['name', 'anschrift'],
      properties: {
        name: {
          type: 'string',
          minLength: 1,
          maxLength: 200,
          title: 'Name des Mieters',
          description: 'Vollständiger Name bzw. Firmenwortlaut der mietenden Partei.',
        },
        anschrift: {
          type: 'string',
          minLength: 1,
          maxLength: 300,
          title: 'Anschrift des Mieters',
          description: 'Zustelladresse des Mieters (Straße, Hausnummer, PLZ, Ort).',
        },
      },
    },
    objekt: {
      type: 'object',
      title: 'Mietobjekt',
      description: 'Beschreibung des Bestandobjekts.',
      required: ['adresse', 'art', 'flaeche_m2'],
      properties: {
        adresse: {
          type: 'string',
          minLength: 1,
          maxLength: 300,
          title: 'Adresse des Mietobjekts',
          description: 'Lage des Mietobjekts (Straße, Hausnummer, PLZ, Ort).',
        },
        art: {
          type: 'string',
          enum: ['Wohnung', 'Geschäftsraum'],
          title: 'Art des Mietobjekts',
          description:
            'Nutzungsart des Mietobjekts. Zulässig: „Wohnung" (Wohnzwecke) oder „Geschäftsraum" (Geschäfts-/Büro-/Gewerbezwecke).',
        },
        flaeche_m2: {
          type: 'number',
          exclusiveMinimum: 0,
          maximum: 100000,
          title: 'Nutzfläche in m²',
          description: 'Nutzfläche des Mietobjekts in Quadratmetern (z. B. 72,5).',
        },
        top: {
          type: 'string',
          maxLength: 40,
          title: 'Top-/Objektnummer',
          description: 'Optionale Top-, Tür- oder Objektnummer (z. B. „Top 14"). Leer lassen, wenn nicht zutreffend.',
        },
      },
    },
    mietbeginn: {
      type: 'string',
      format: 'date',
      title: 'Mietbeginn',
      description: 'Beginn des Mietverhältnisses (z. B. 01.09.2026). Wird im Dokument als TT.MM.JJJJ ausgegeben.',
    },
    befristet: {
      type: 'boolean',
      title: 'Befristetes Mietverhältnis?',
      description: 'Ja, wenn das Mietverhältnis befristet abgeschlossen wird. Bei „ja" ist das Enddatum erforderlich.',
    },
    befristung_bis: {
      type: 'string',
      format: 'date',
      title: 'Befristet bis',
      description: 'Enddatum bei befristetem Mietverhältnis (z. B. 31.08.2029). Nur erforderlich, wenn befristet = ja.',
    },
    hauptmietzins_eur: {
      type: 'number',
      minimum: 0,
      maximum: 10000000,
      title: 'Hauptmietzins (EUR/Monat)',
      description: 'Monatlicher Hauptmietzins (Netto-Miete ohne Betriebskosten) in Euro.',
    },
    betriebskosten_eur: {
      type: 'number',
      minimum: 0,
      maximum: 10000000,
      title: 'Betriebskosten-Vorauszahlung (EUR/Monat)',
      description: 'Monatliche Betriebskosten-Vorauszahlung in Euro (Akontozahlung; Jahresabrechnung gesondert).',
    },
    wertsicherung: {
      type: 'boolean',
      default: false,
      title: 'Wertsicherung vereinbart?',
      description: 'Ja, wenn eine Wertsicherung (Indexanpassung) vereinbart wird.',
    },
    wertsicherung_index: {
      type: 'string',
      maxLength: 120,
      title: 'Wertsicherungs-Index',
      description: 'Bezeichnung des Index, z. B. „Verbraucherpreisindex 2020 (VPI 2020)". Nur relevant bei Wertsicherung.',
    },
    kaution_eur: {
      type: 'number',
      minimum: 0,
      maximum: 10000000,
      title: 'Kaution (EUR)',
      description: 'Vereinbarte Kaution als Geldbetrag in Euro. Optional.',
    },
    kaution_monate: {
      type: 'number',
      minimum: 0,
      maximum: 24,
      title: 'Kaution (Anzahl Monatsmieten)',
      description: 'Vereinbarte Kaution als Anzahl Bruttomonatsmieten (z. B. 3). Optional.',
    },
    sonderklauseln: {
      type: 'array',
      title: 'Sonderklauseln',
      description:
        'Optionale Sonderklauseln (Mehrfachauswahl). Jede gewählte Klausel wird als markierter Entwurfs-Stub eingefügt ([ENTWURF – anwaltlich zu prüfen]).',
      items: {
        type: 'string',
        enum: ['haustiere', 'untermiete', 'erhaltung_mieter', 'bauliche_aenderungen', 'rueckstellung'],
        title: 'Sonderklausel',
      },
    },
  },
  if: { properties: { befristet: { const: true } }, required: ['befristet'] },
  then: { required: ['befristung_bis'] },
};

/** Lesbare Labels für die Sonderklausel-Enum-Schlüssel. */
export const SONDERKLAUSEL_LABELS: Record<string, string> = {
  haustiere: 'Tierhaltung',
  untermiete: 'Untervermietung / Weitergabe',
  erhaltung_mieter: 'Erhaltungs-/Instandhaltungspflichten des Mieters',
  bauliche_aenderungen: 'Bauliche Veränderungen / Investitionen',
  rueckstellung: 'Zustand bei Rückstellung',
};

/** Top-Level-Keys, die der n8n-Engine-Mietvertrag akzeptiert (schema JSON). */
export const MIETVERTRAG_ENGINE_TOP_KEYS = [
  'vermieter',
  'mieter',
  'objekt',
  'mietbeginn',
  'befristet',
  'befristung_bis',
  'hauptmietzins_eur',
  'betriebskosten_eur',
  'wertsicherung',
  'wertsicherung_index',
  'kaution_eur',
  'kaution_monate',
  'sonderklauseln',
  'individuell',
] as const;

export const MIETVERTRAG_TEMPLATE: ContractTemplateDef = {
  id: 'mietvertrag',
  label: 'Mietvertrag (Österreich)',
  blurb: 'Wohnung oder Geschäftsraum · Server-Entwurf · Bausteine an/aus',
  schema: MIETVERTRAG_SCHEMA,
  maturity: 'engine',
  clauseLabels: SONDERKLAUSEL_LABELS,
  blocks: [
    {
      id: 'parteien',
      label: 'Parteien',
      hint: 'Vermieter und Mieter',
      fieldKeys: ['vermieter', 'mieter'],
      defaultEnabled: true,
      locked: true,
    },
    {
      id: 'objekt',
      label: 'Mietobjekt',
      hint: 'Adresse, Art, Fläche',
      fieldKeys: ['objekt'],
      defaultEnabled: true,
      locked: true,
    },
    {
      id: 'dauer',
      label: 'Beginn und Dauer',
      hint: 'Mietbeginn, Befristung',
      fieldKeys: ['mietbeginn', 'befristet', 'befristung_bis'],
      defaultEnabled: true,
      locked: true,
    },
    {
      id: 'mietzins',
      label: 'Mietzins und Betriebskosten',
      hint: 'Hauptmietzins und Akonto',
      fieldKeys: ['hauptmietzins_eur', 'betriebskosten_eur'],
      defaultEnabled: true,
      locked: true,
    },
    {
      id: 'wertsicherung',
      label: 'Wertsicherung',
      hint: 'Indexanpassung — Baustein an/aus',
      fieldKeys: ['wertsicherung', 'wertsicherung_index'],
      defaultEnabled: false,
      bindsBoolean: 'wertsicherung',
    },
    {
      id: 'kaution',
      label: 'Kaution',
      hint: 'Betrag oder Monatsmieten',
      fieldKeys: ['kaution_eur', 'kaution_monate'],
      defaultEnabled: true,
    },
    {
      id: 'sonderklauseln',
      label: 'Sonderklauseln (Stubs)',
      hint: 'Nur markierte Entwurfs-Stubs, kein Kanzlei-Muster',
      fieldKeys: ['sonderklauseln'],
      defaultEnabled: true,
    },
    {
      id: 'moebliert',
      label: 'Möbliert / Inventar',
      hint: 'Genauere Option — Muster folgt',
      fieldKeys: [],
      defaultEnabled: false,
      extraOnly: true,
    },
    {
      id: 'stellplatz',
      label: 'KFZ-Stellplatz',
      hint: 'Genauere Option — Muster folgt',
      fieldKeys: [],
      defaultEnabled: false,
      extraOnly: true,
    },
    {
      id: 'hausordnung',
      label: 'Hausordnung',
      hint: 'Muster folgt',
      fieldKeys: [],
      defaultEnabled: false,
      extraOnly: true,
    },
    {
      id: 'kuendigung',
      label: 'Kündigung / Frist',
      hint: 'Genauere Option — Muster folgt',
      fieldKeys: [],
      defaultEnabled: false,
      extraOnly: true,
    },
  ],
  extraSlots: [
    {
      id: 'nutzung_detail',
      blockId: 'objekt',
      label: 'Genauere Nutzungsart',
      kind: 'enum',
      enum: ['—', 'Einfamilienhaus', 'Stellplatz', 'Lager', 'Sonstiges'],
      placeholder: 'Ergänzt Wohnung/Geschäftsraum; Server-Entwurf nutzt weiter nur Wohnung/Geschäftsraum.',
    },
    {
      id: 'moebliert_notiz',
      blockId: 'moebliert',
      label: 'Möblierung — Notiz',
      kind: 'string',
      placeholder: 'Muster folgt — Kanzlei-Text einfügen (kein ARKORBIT-Standard).',
    },
    {
      id: 'stellplatz_notiz',
      blockId: 'stellplatz',
      label: 'Stellplatz — Notiz',
      kind: 'string',
      placeholder: 'Muster folgt — Kanzlei-Text einfügen (kein ARKORBIT-Standard).',
    },
    {
      id: 'hausordnung_notiz',
      blockId: 'hausordnung',
      label: 'Hausordnung — Notiz',
      kind: 'string',
      placeholder: 'Muster folgt — Kanzlei-Text einfügen (kein ARKORBIT-Standard).',
    },
    {
      id: 'kuendigung_frist',
      blockId: 'kuendigung',
      label: 'Kündigungsfrist (Stichwort)',
      kind: 'enum',
      enum: ['—', '1 Monat', '3 Monate', '6 Monate', 'gesetzlich / offen'],
    },
    {
      id: 'kuendigung_notiz',
      blockId: 'kuendigung',
      label: 'Kündigung — Notiz',
      kind: 'string',
      placeholder: 'Muster folgt — Kanzlei-Text einfügen (kein ARKORBIT-Standard).',
    },
  ],
};

export const CONTRACT_TEMPLATES: ContractTemplateDef[] = [MIETVERTRAG_TEMPLATE, ...GERUEST_TEMPLATES];

export function isEngineTemplate(id: string): boolean {
  return getContractTemplate(id)?.maturity === 'engine';
}

export function defaultEnabledBlockIds(template: ContractTemplateDef): string[] {
  return template.blocks.filter((b) => b.locked || b.defaultEnabled).map((b) => b.id);
}

export function isBlockActive(template: ContractTemplateDef, enabled: string[], blockId: string): boolean {
  const block = template.blocks.find((b) => b.id === blockId);
  if (!block) return false;
  if (block.locked) return true;
  return enabled.includes(blockId);
}

/** Schema nur mit Feldern der aktiven Bausteine (Validierung + Formular). */
export function schemaForEnabledBlocks(
  template: ContractTemplateDef,
  enabledBlockIds: string[],
): ContractSchema {
  const allowed = new Set<string>();
  for (const b of template.blocks) {
    if (b.locked || enabledBlockIds.includes(b.id)) {
      for (const k of b.fieldKeys) allowed.add(k);
    }
  }
  const properties: Record<string, SchemaProp> = {};
  for (const [k, v] of Object.entries(template.schema.properties)) {
    if (allowed.has(k)) properties[k] = v;
  }
  return {
    ...template.schema,
    properties,
    required: (template.schema.required || []).filter((k) => allowed.has(k)),
  };
}

/**
 * Submit-Body für die Engine: nur Keys aus mietvertrag.schema.json.
 * Extra-Slots bleiben lokal. Individuell = nur User-Text (kein Stub).
 */
export function buildEngineSubmitFields(
  template: ContractTemplateDef,
  form: ContractValues,
  enabledBlockIds: string[],
  eigeneKlauseln?: EigeneKlausel[],
): ContractValues {
  const scoped = schemaForEnabledBlocks(template, enabledBlockIds);
  const raw = buildSubmitFields(scoped, form);
  if (template.id !== 'mietvertrag') return raw;
  let out: ContractValues = {};
  for (const key of MIETVERTRAG_ENGINE_TOP_KEYS) {
    if (key === 'individuell') continue;
    if (raw[key] !== undefined) out[key] = raw[key];
  }
  const individuell = individuellOutputItems(eigeneKlauseln);
  if (individuell.length) out.individuell = individuell;
  return out;
}

export function getContractTemplate(id: string): ContractTemplateDef | undefined {
  return CONTRACT_TEMPLATES.find((t) => t.id === id);
}

// ── Generische Schema-Helfer ──────────────────────────────────────────────────

export interface LeafField {
  /** Dot-Pfad, z. B. "vermieter.name" oder "mietbeginn". */
  path: string;
  /** Anzeigeschlüssel des Blatts (letzter Pfad-Teil). */
  key: string;
  /** Optionaler Gruppentitel (Titel des umschließenden Objekts) für die UI. */
  groupTitle?: string;
  prop: SchemaProp;
  /** Basis-Pflicht (ohne if/then). */
  baseRequired: boolean;
  /** Aktiv nur, wenn ein anderes Feld einen Wert hat (if/then). */
  conditional?: { whenPath: string; equals: unknown };
}

/** Flache, geordnete Liste aller Blattfelder (eine Objektebene tief, wie das Schema). */
export function collectLeafFields(schema: ContractSchema): LeafField[] {
  const out: LeafField[] = [];
  const topRequired = schema.required || [];

  for (const [key, prop] of Object.entries(schema.properties)) {
    if (prop.type === 'object' && prop.properties) {
      const subRequired = prop.required || [];
      for (const [subKey, subProp] of Object.entries(prop.properties)) {
        out.push({
          path: `${key}.${subKey}`,
          key: subKey,
          groupTitle: prop.title,
          prop: subProp,
          baseRequired: topRequired.includes(key) && subRequired.includes(subKey),
        });
      }
    } else {
      out.push({
        path: key,
        key,
        prop,
        baseRequired: topRequired.includes(key),
      });
    }
  }

  // if/then: Felder aus then.required, die nicht bereits Pflicht sind, werden bedingt.
  const thenRequired = schema.then?.required || [];
  const ifProps = schema.if?.properties || {};
  const whenEntry = Object.entries(ifProps)[0];
  if (whenEntry) {
    const [whenPath, cond] = whenEntry;
    for (const f of out) {
      if (thenRequired.includes(f.path) && !f.baseRequired) {
        f.conditional = { whenPath, equals: cond.const };
      }
    }
  }
  return out;
}

export type ContractValues = Record<string, unknown>;

export function getByPath(values: ContractValues, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, part) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[part];
    return undefined;
  }, values);
}

/** Immutable: setzt value an dot-Pfad (legt Zwischenobjekte an). */
export function setByPath(values: ContractValues, path: string, value: unknown): ContractValues {
  const parts = path.split('.');
  const next: ContractValues = { ...values };
  let cursor = next;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    const existing = cursor[part];
    const clone = existing && typeof existing === 'object' ? { ...(existing as Record<string, unknown>) } : {};
    cursor[part] = clone;
    cursor = clone as ContractValues;
  }
  cursor[parts[parts.length - 1]] = value;
  return next;
}

/** Ist ein bedingtes Feld aktuell aktiv (if-Bedingung erfüllt)? */
export function isConditionalActive(field: LeafField, values: ContractValues): boolean {
  if (!field.conditional) return true;
  return getByPath(values, field.conditional.whenPath) === field.conditional.equals;
}

/** Effektive Pflicht inkl. if/then. */
export function isEffectivelyRequired(field: LeafField, values: ContractValues): boolean {
  if (field.baseRequired) return true;
  if (field.conditional) return isConditionalActive(field, values);
  return false;
}

// ── Antwort-Parser (typabhängig) ─────────────────────────────────────────────

export interface ParseResult {
  ok: boolean;
  /** Gesetzt bei ok=true (typkonvertierter Wert). */
  value?: unknown;
  /** Gesetzt bei ok=false (Validierungsfehler für die UI/den Chat). */
  error?: string;
}

const TRUE_WORDS = ['ja', 'j', 'yes', 'y', 'true', 'wahr', 'stimmt', 'befristet'];
const FALSE_WORDS = ['nein', 'n', 'no', 'false', 'falsch', 'unbefristet', 'keine', 'kein'];

function parseGermanNumber(raw: string): number | null {
  const cleaned = raw.replace(/[^0-9.,-]/g, '').trim();
  if (!cleaned) return null;
  let normalized = cleaned;
  if (cleaned.includes('.') && cleaned.includes(',')) {
    // "1.250,50" → Punkt = Tausender, Komma = Dezimal
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  } else if (cleaned.includes(',')) {
    normalized = cleaned.replace(',', '.');
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/** Normalisiert ein Datum (ISO JJJJ-MM-TT oder TT.MM.JJJJ) auf ISO; null bei ungültig. */
export function normalizeDateToIso(raw: string): string | null {
  const t = raw.trim();
  let m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    return isValidYmd(+m[1], +m[2], +m[3]) ? `${m[1]}-${m[2]}-${m[3]}` : null;
  }
  m = t.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (m) {
    const d = +m[1];
    const mo = +m[2];
    const y = +m[3];
    if (!isValidYmd(y, mo, d)) return null;
    return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  return null;
}

function isValidYmd(y: number, mo: number, d: number): boolean {
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return false;
  const dt = new Date(Date.UTC(y, mo - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d;
}

/** Validiert/konvertiert eine Roh-Eingabe für ein Blattfeld. */
export function parseFieldAnswer(prop: SchemaProp, raw: string): ParseResult {
  const text = raw.trim();
  if (!text) return { ok: false, error: 'Bitte eine Eingabe machen.' };

  if (prop.type === 'boolean') {
    const low = text.toLowerCase();
    if (TRUE_WORDS.some((w) => low === w || low.startsWith(w + ' '))) return { ok: true, value: true };
    if (FALSE_WORDS.some((w) => low === w || low.startsWith(w + ' '))) return { ok: true, value: false };
    return { ok: false, error: 'Bitte mit „ja" oder „nein" antworten.' };
  }

  if (prop.enum && prop.enum.length) {
    const low = text.toLowerCase();
    const hit = prop.enum.find((e) => e.toLowerCase() === low || low.includes(e.toLowerCase()));
    if (hit) return { ok: true, value: hit };
    return { ok: false, error: `Bitte eine der Optionen wählen: ${prop.enum.join(', ')}.` };
  }

  if (prop.type === 'number') {
    const n = parseGermanNumber(text);
    if (n === null) return { ok: false, error: 'Bitte eine Zahl eingeben (z. B. 1250 oder 72,5).' };
    if (typeof prop.minimum === 'number' && n < prop.minimum) return { ok: false, error: `Wert muss ≥ ${prop.minimum} sein.` };
    if (typeof prop.exclusiveMinimum === 'number' && n <= prop.exclusiveMinimum)
      return { ok: false, error: `Wert muss größer als ${prop.exclusiveMinimum} sein.` };
    if (typeof prop.maximum === 'number' && n > prop.maximum) return { ok: false, error: `Wert muss ≤ ${prop.maximum} sein.` };
    return { ok: true, value: n };
  }

  if (prop.format === 'date') {
    const iso = normalizeDateToIso(text);
    if (!iso) return { ok: false, error: 'Bitte ein Datum eingeben (z. B. 01.09.2026 oder 2026-09-01).' };
    return { ok: true, value: iso };
  }

  // string
  if (typeof prop.minLength === 'number' && text.length < prop.minLength)
    return { ok: false, error: `Bitte mindestens ${prop.minLength} Zeichen.` };
  if (typeof prop.maxLength === 'number' && text.length > prop.maxLength)
    return { ok: false, error: `Bitte höchstens ${prop.maxLength} Zeichen.` };
  return { ok: true, value: text };
}

// ── Formular-Werte → Submit-Body ─────────────────────────────────────────────

/** Initiale Formularwerte (UI-State): strings '' / boolean false / arrays []. */
export function buildInitialFormValues(schema: ContractSchema): ContractValues {
  const init: ContractValues = {};
  for (const [key, prop] of Object.entries(schema.properties)) {
    if (prop.type === 'object' && prop.properties) {
      const sub: ContractValues = {};
      for (const [subKey, subProp] of Object.entries(prop.properties)) {
        sub[subKey] = formDefaultFor(subProp);
      }
      init[key] = sub;
    } else {
      init[key] = formDefaultFor(prop);
    }
  }
  return init;
}

function formDefaultFor(prop: SchemaProp): unknown {
  if (prop.type === 'boolean') return prop.default === true;
  if (prop.type === 'array') return [];
  return '';
}

/**
 * Baut den `fields`-Body aus den Formularwerten gemäß Schema:
 * - Zahlen-Strings → number
 * - leere Optionalfelder werden weggelassen
 * - inaktive bedingte Felder (z. B. befristung_bis bei unbefristet) werden weggelassen
 */
export function buildSubmitFields(schema: ContractSchema, form: ContractValues): ContractValues {
  const leafs = collectLeafFields(schema);
  let out: ContractValues = {};

  for (const f of leafs) {
    if (f.conditional && !isConditionalActive(f, form)) continue;
    const rawVal = getByPath(form, f.path);
    const converted = convertFormValue(f.prop, rawVal);
    if (converted === undefined) {
      if (f.baseRequired || (f.conditional && isConditionalActive(f, form))) {
        // Pflichtfeld fehlt — Aufrufer validiert separat; hier nicht setzen.
      }
      continue;
    }
    out = setByPath(out, f.path, converted);
  }
  return out;
}

function convertFormValue(prop: SchemaProp, raw: unknown): unknown {
  if (prop.type === 'boolean') return raw === true;
  if (prop.type === 'array') {
    const arr = Array.isArray(raw) ? raw.filter((x) => typeof x === 'string' && x) : [];
    return arr.length ? arr : undefined;
  }
  if (prop.type === 'number') {
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
    const s = typeof raw === 'string' ? raw.trim() : '';
    if (!s) return undefined;
    const n = parseGermanNumber(s);
    return n === null ? undefined : n;
  }
  // string / date
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s) return undefined;
  if (prop.format === 'date') {
    return normalizeDateToIso(s) ?? s;
  }
  return s;
}

export interface ValidationResult {
  ok: boolean;
  errors: Record<string, string>;
}

/** Client-Validierung gegen required + if/then + Typ/Range. */
export function validateContractForm(schema: ContractSchema, form: ContractValues): ValidationResult {
  const leafs = collectLeafFields(schema);
  const errors: Record<string, string> = {};

  for (const f of leafs) {
    const active = isConditionalActive(f, form);
    if (!active) continue;
    const required = isEffectivelyRequired(f, form);
    const raw = getByPath(form, f.path);

    if (f.prop.type === 'boolean') continue; // boolean immer „gesetzt" (ja/nein)
    if (f.prop.type === 'array') continue; // optional

    const s = typeof raw === 'string' ? raw.trim() : raw == null ? '' : String(raw);
    if (!s) {
      if (required) errors[f.path] = 'Pflichtfeld.';
      continue;
    }
    const parsed = parseFieldAnswer(f.prop, s);
    if (!parsed.ok) errors[f.path] = parsed.error ?? 'Ungültige Eingabe.';
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

/** Menschlich lesbare Frage für ein Blattfeld (Slot-Filling). */
export function questionForField(f: LeafField): string {
  const t = f.prop.title || f.key;
  const groupHint = f.groupTitle && !t.toLowerCase().includes(f.groupTitle.toLowerCase()) ? ` (${f.groupTitle})` : '';
  if (f.prop.type === 'boolean') {
    return `${t}${groupHint} — bitte mit „ja" oder „nein" antworten.`;
  }
  if (f.prop.enum && f.prop.enum.length) {
    return `${t}${groupHint} — bitte wählen: ${f.prop.enum.join(' oder ')}.`;
  }
  if (f.prop.format === 'date') {
    return `${t}${groupHint} — bitte als Datum (z. B. 01.09.2026).`;
  }
  if (f.prop.type === 'number') {
    return `${t}${groupHint} — bitte als Zahl (z. B. ${f.key.includes('flaeche') ? '72,5' : '1250'}).`;
  }
  return `Wie lautet: ${t}${groupHint}?`;
}
